package fr.frux.feature.onboarding

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Groups
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material.icons.outlined.PinDrop
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.layout.FruxLayoutMode
import fr.frux.core.designsystem.layout.LocalFruxLayoutMode
import fr.frux.core.designsystem.layout.SplitEntryLayout
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.maps.LocationPermissionStatus
import fr.frux.core.maps.rememberLocationPermissionController
import fr.frux.feature.onboarding.resources.Res
import fr.frux.feature.onboarding.resources.onboarding_body
import fr.frux.feature.onboarding.resources.onboarding_disclaimer
import fr.frux.feature.onboarding.resources.onboarding_headline_find
import fr.frux.feature.onboarding.resources.onboarding_headline_share
import fr.frux.feature.onboarding.resources.onboarding_location_allow
import fr.frux.feature.onboarding.resources.onboarding_location_body
import fr.frux.feature.onboarding.resources.onboarding_location_later
import fr.frux.feature.onboarding.resources.onboarding_location_title
import fr.frux.feature.onboarding.resources.onboarding_login
import fr.frux.feature.onboarding.resources.onboarding_start
import fr.frux.feature.onboarding.resources.onboarding_step_place
import fr.frux.feature.onboarding.resources.onboarding_step_share
import fr.frux.feature.onboarding.resources.onboarding_step_spot
import org.jetbrains.compose.resources.stringResource

/**
 * Accueil de première ouverture (#502), en deux pas : la promesse (et la vérité sur la
 * couverture), puis la position, demandée sur un geste explicite pour marcher aussi sur le web.
 *
 * @param locationStep Faux quand la plateforme n'a pas de position à demander (desktop) : la
 * promesse termine alors directement. Décidé par l'hôte, le module ne connaît pas la plateforme.
 * @param onFinished Fin de l'accueil, avec ce que la carte doit en savoir ([OnboardingOutcome]).
 * @param brandVisual Visuel de marque du panneau gauche en grand écran, fourni par l'hôte.
 * @param logo Logo de tête en disposition compacte, fourni par l'hôte pour la même raison.
 */
@Composable
fun OnboardingScreen(
    locationStep: Boolean,
    onFinished: (OnboardingOutcome) -> Unit,
    onLogin: () -> Unit,
    modifier: Modifier = Modifier,
    brandVisual: @Composable BoxScope.() -> Unit = {},
    logo: @Composable (Modifier) -> Unit = {},
) {
    var step by rememberSaveable { mutableStateOf(OnboardingStep.PROMISE) }
    val expanded = LocalFruxLayoutMode.current == FruxLayoutMode.Expanded

    OnboardingBackHandler(enabled = step == OnboardingStep.LOCATION) { step = OnboardingStep.PROMISE }

    SplitEntryLayout(visual = brandVisual, modifier = modifier) {
        // Seul écran plein cadre sans `Scaffold` (qui consommerait les insets via ses
        // `paddingValues`) : sous edge-to-edge, le lien « Se connecter » collerait sinon à la barre
        // de gestes et le haut passerait sous la status bar.
        BoxWithConstraints(
            modifier =
                Modifier
                    .fillMaxSize()
                    .windowInsetsPadding(WindowInsets.safeDrawing),
        ) {
            // Petit téléphone : le logo rétrécit, et si le texte déborde encore, l'écran défile
            // plutôt que de couper les boutons (constaté à 360 × 640).
            val logoSize = if (maxHeight < COMPACT_HEIGHT) LOGO_SIZE_COMPACT else LOGO_SIZE
            Column(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState())
                        .heightIn(min = maxHeight)
                        .padding(horizontal = FruxSpacing.lg, vertical = FruxSpacing.xl),
                verticalArrangement = Arrangement.SpaceEvenly,
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                // Logo de tête : en grand écran le visuel de marque occupe déjà le panneau gauche.
                if (!expanded) {
                    logo(Modifier.size(logoSize))
                }

                when (step) {
                    OnboardingStep.PROMISE -> {
                        PromiseStep(
                            onContinue = {
                                val next = nextStepAfterPromise(locationStep)
                                if (next == null) onFinished(OnboardingOutcome.Finished) else step = next
                            },
                            onLogin = onLogin,
                        )
                    }

                    OnboardingStep.LOCATION -> {
                        LocationStep(onFinished = onFinished)
                    }
                }
            }
        }
    }
}

private val COMPACT_HEIGHT = 720.dp
private val LOGO_SIZE = 160.dp
private val LOGO_SIZE_COMPACT = 104.dp

/**
 * La promesse (spec onboarding de contribution §1) : titre de marque, le modèle contributif en une
 * phrase, puis trois gestes numérotés. La note de sécurité reste présente mais discrète, pour ne
 * pas empiler quatre messages de même poids. Le CTA est le même avec ou sans pas position.
 */
@Composable
private fun PromiseStep(
    onContinue: () -> Unit,
    onLogin: () -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = stringResource(Res.string.onboarding_headline_find),
            style = MaterialTheme.typography.headlineLarge,
            color = MaterialTheme.colorScheme.onSurface,
            textAlign = TextAlign.Center,
        )
        Text(
            text = stringResource(Res.string.onboarding_headline_share),
            style = MaterialTheme.typography.headlineLarge,
            color = MaterialTheme.colorScheme.primary,
            textAlign = TextAlign.Center,
        )

        Spacer(modifier = Modifier.height(FruxSpacing.sm))

        Text(
            text = stringResource(Res.string.onboarding_body),
            style = MaterialTheme.typography.bodyLarge,
            textAlign = TextAlign.Center,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.padding(horizontal = FruxSpacing.sm),
        )

        Spacer(modifier = Modifier.height(FruxSpacing.md))

        Column(verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs)) {
            ContributionStepCard(
                number = 1,
                icon = Icons.Outlined.Park,
                text = stringResource(Res.string.onboarding_step_spot),
            )
            ContributionStepCard(
                number = 2,
                icon = Icons.Outlined.PinDrop,
                text = stringResource(Res.string.onboarding_step_place),
            )
            ContributionStepCard(
                number = 3,
                icon = Icons.Outlined.Groups,
                text = stringResource(Res.string.onboarding_step_share),
            )
        }

        Spacer(modifier = Modifier.height(FruxSpacing.md))

        // Mention de responsabilité (ADR 0015, #235) : l'un des trois porteurs obligatoires,
        // avec le pied de fiche arbre et la page Sécurité du site.
        Text(
            text = stringResource(Res.string.onboarding_disclaimer),
            style = MaterialTheme.typography.bodySmall,
            textAlign = TextAlign.Center,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.padding(horizontal = FruxSpacing.sm),
        )

        Spacer(modifier = Modifier.height(FruxSpacing.lg))

        FruxButton(
            text = stringResource(Res.string.onboarding_start),
            onClick = onContinue,
            style = FruxButtonStyle.Filled,
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = FruxSpacing.lg),
        )

        Spacer(modifier = Modifier.height(FruxSpacing.sm))

        FruxButton(
            text = stringResource(Res.string.onboarding_login),
            onClick = onLogin,
            style = FruxButtonStyle.Text,
        )
    }
}

/** Un geste du modèle contributif : numéro, pictogramme, phrase. Lu comme un seul élément. */
@Composable
private fun ContributionStepCard(
    number: Int,
    icon: ImageVector,
    text: String,
) {
    Surface(
        shape = MaterialTheme.shapes.medium,
        color = MaterialTheme.colorScheme.surfaceVariant,
        modifier =
            Modifier
                .fillMaxWidth()
                .semantics(mergeDescendants = true) {},
    ) {
        Row(
            modifier = Modifier.padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            Box(
                modifier =
                    Modifier
                        .size(STEP_BADGE_SIZE)
                        .clip(CircleShape)
                        .background(MaterialTheme.colorScheme.primary),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = number.toString(),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.onPrimary,
                )
            }
            Icon(imageVector = icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
            Text(
                text = text,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurface,
            )
        }
    }
}

private val STEP_BADGE_SIZE = 28.dp

@Composable
private fun LocationStep(onFinished: (OnboardingOutcome) -> Unit) {
    val permission = rememberLocationPermissionController()

    // La réponse arrive par le statut, pas par un callback du bouton : sur le web la boîte de
    // dialogue est celle du navigateur, et sur Android celle du système. Se déclenche aussi à
    // l'arrivée : une permission déjà accordée par le système termine sans clic, il n'y a rien
    // à demander.
    LaunchedEffect(permission.status) {
        outcomeForLocationStatus(permission.status)?.let(onFinished)
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = stringResource(Res.string.onboarding_location_title),
            style = MaterialTheme.typography.displayMedium,
            color = MaterialTheme.colorScheme.onSurface,
            textAlign = TextAlign.Center,
        )

        Spacer(modifier = Modifier.height(FruxSpacing.md))

        Text(
            text = stringResource(Res.string.onboarding_location_body),
            style = MaterialTheme.typography.bodyLarge,
            textAlign = TextAlign.Center,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.padding(horizontal = FruxSpacing.sm),
        )

        Spacer(modifier = Modifier.height(FruxSpacing.xl))

        FruxButton(
            text = stringResource(Res.string.onboarding_location_allow),
            onClick = {
                if (permission.status == LocationPermissionStatus.NOT_REQUESTED) {
                    permission.request()
                } else {
                    outcomeForLocationStatus(permission.status)?.let(onFinished)
                }
            },
            style = FruxButtonStyle.Filled,
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = FruxSpacing.lg),
        )

        Spacer(modifier = Modifier.height(FruxSpacing.md))

        FruxButton(
            text = stringResource(Res.string.onboarding_location_later),
            onClick = { onFinished(OnboardingOutcome.LocationDeclined) },
            style = FruxButtonStyle.Text,
        )
    }
}
